import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface PaymentData {
  success: boolean;
  userExists: boolean;
  user: {
    id: string;
    email: string;
  };
  planData: {
    planId: string;
    planName: string;
    customerId: string;
    subscriptionId: string;
  };
}

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setError('ID da sessão não encontrado');
      setLoading(false);
      return;
    }

    verifyPaymentAndCreateAccount();
  }, [sessionId]);

  const verifyPaymentAndCreateAccount = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('verify-payment-and-create-account', {
        body: { sessionId }
      });

      if (error) throw error;

      setPaymentData(data);
      
      if (data.success) {
        toast({
          title: "Pagamento confirmado!",
          description: data.userExists 
            ? "Sua conta já existe. Agora vamos configurar sua organização."
            : "Conta criada com sucesso! Agora vamos configurar sua organização.",
        });
      }

    } catch (error: any) {
      console.error('Erro ao verificar pagamento:', error);
      setError(error.message || 'Erro ao verificar pagamento');
      toast({
        title: "Erro na verificação",
        description: "Não foi possível verificar o pagamento. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    if (!paymentData) return;

    try {
      // Sempre salvar os dados no localStorage, independente de o usuário existir ou não
      const pendingData = {
        userId: paymentData.user.id,
        planData: paymentData.planData,
        sessionId
      };
      
      console.log('Salvando dados no localStorage:', pendingData);
      localStorage.setItem('pendingOrgCreation', JSON.stringify(pendingData));
      
      // Verificar se foi salvo corretamente
      const saved = localStorage.getItem('pendingOrgCreation');
      console.log('Dados salvos confirmados:', saved);

      // Redirecionar para página de criação de organização
      navigate('/create-organization-after-payment');

    } catch (error: any) {
      console.error('Erro ao continuar:', error);
      toast({
        title: "Erro",
        description: "Erro ao prosseguir. Tente novamente.",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-6">
            <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Verificando pagamento...</h2>
            <p className="text-muted-foreground">
              Por favor, aguarde enquanto confirmamos seu pagamento e criamos sua conta.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-6">
            <div className="text-destructive text-4xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold mb-2">Erro na verificação</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate('/')} variant="outline">
              Voltar ao início
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!paymentData?.success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-6">
            <div className="text-destructive text-4xl mb-4">❌</div>
            <h2 className="text-xl font-semibold mb-2">Pagamento não confirmado</h2>
            <p className="text-muted-foreground mb-4">
              Não foi possível confirmar seu pagamento. Entre em contato conosco.
            </p>
            <Button onClick={() => navigate('/')} variant="outline">
              Voltar ao início
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="max-w-md w-full text-center">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <CheckCircle className="w-16 h-16 text-green-500" />
          </div>
          <CardTitle className="text-2xl text-green-600">
            Pagamento Confirmado!
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <p className="text-muted-foreground">
              {paymentData.userExists 
                ? "Seu pagamento foi processado com sucesso!"
                : "Sua conta foi criada e o pagamento foi processado com sucesso!"
              }
            </p>
            
            <div className="bg-muted p-3 rounded-lg text-sm">
              <p><strong>Plano:</strong> {paymentData.planData.planName}</p>
              <p><strong>Email:</strong> {paymentData.user.email}</p>
            </div>
          </div>

          <div className="space-y-2">
            <p className="font-medium">Próximo passo:</p>
            <p className="text-sm text-muted-foreground">
              Agora vamos configurar sua organização para que você possa começar a usar a plataforma.
            </p>
          </div>
          
          <Button 
            onClick={handleContinue}
            className="w-full"
          >
            Configurar Organização
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}